/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { ALERT_TYPE_REPORT, Alert } from '@squiz/sds';
import {
  EntityLog,
  EntityLogsResponse,
  LogSortDirection,
  getLoggingService,
} from 'apps/console-core/src/Services/logging';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { MONITORING_STRINGS } from '../../../../Constants/Strings.constants';
import { LogsQuery } from '../../Logs.constants';
import { getDateRangeFromSearchParams } from '../../utils/GetDateRangeFromSearchParams/GetDateRangeFromSearchParams';
import { LogsFilter } from '../LogsFilter/LogsFilter';
import { LogsFilterProps } from '../LogsFilter/LogsFilter.props';
import { LogsTable } from '../LogsTable/LogsTable';
import { LogsTableProps } from '../LogsTable/LogsTable.props';

/**
 * Controller to render the required components for the logging screen.
 */
export const LogsTableController = (): JSX.Element => {
  const { t } = useTranslation();

  // URL search params
  const [searchParams] = useSearchParams();

  // Logging service
  const loggingService = getLoggingService();

  // Text values
  const searchText = searchParams.get(LogsQuery.filter.query) as string;
  const filterTag = searchParams.get(LogsQuery.filter.tag) as string;

  // Managing loading and refresh behaviour
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [error, setError] = useState<string>('');

  // Logs
  const [logs, setLogs] = useState<Array<EntityLog>>([]);

  // Labels
  const labels = useMemo(() => {
    return {
      error: t(MONITORING_STRINGS.error),
      invalidDateParams: t(MONITORING_STRINGS.invalidDateParams),
    };
  }, [t]);

  // Retrieve logs
  const getLogs = useCallback(
    (controller?: AbortController): void => {
      setLoading(true);

      // Get date range
      const [dateRange, dateRangeError] = getDateRangeFromSearchParams(searchParams);
      setError(dateRangeError ? labels.invalidDateParams : '');

      // Convert into the required format
      const fromDatetime = new Date(dateRange.startDateTime).toISOString();
      const toDatetime = new Date(dateRange.endDateTime).toISOString();

      lastValueFrom(
        loggingService.getLogs({
          filterFrom: fromDatetime,
          filterTag: filterTag?.split(' '),
          filterTo: toDatetime,
          searchValue: searchText ? encodeURIComponent(searchText) : '',
          sort: {
            direction: LogSortDirection.desc,
            property: 'timestamp',
          },
        }),
      )
        .then((response: EntityLogsResponse | null): void => {
          // Cancel the previous callback function if a subsequent request was made
          if (controller && controller.signal.aborted) {
            return;
          }

          setLoading(false);
          setRefresh(false);

          if (!response) {
            setLogs([]);
          } else {
            setLogs(response.data);
          }

          return;
        })
        .catch((error) => {
          setLoading(false);
          setRefresh(false);

          // Just show regular empty message
          setLogs([]);

          // eslint-disable-next-line no-console
          console.error(error);
        });
    },
    [filterTag, labels.invalidDateParams, loggingService, searchParams, searchText],
  );

  // Make the request for logs after query params are updated
  useEffect(() => {
    // Set up controller object to abort old logs requests.
    // For instance, if a user inputs text for filtering but then decides on a new search
    // while the logs are being still retrieved, we can use this object to stop the old search
    // and avoid unexpected rendering behaviour for this consecutive searches scenario.
    const controller = new AbortController();

    getLogs(controller);

    // Stop the previous request on unmount
    return (): void => controller.abort();
  }, [getLogs, searchParams]);

  // Initiate another logs request when clicking the refresh button
  useEffect(() => {
    if (refresh) {
      getLogs();
    }
  }, [getLogs, refresh]);

  const logsFilterProps: LogsFilterProps = useMemo(() => {
    return {
      setRefresh,
    };
  }, []);

  const logsTableProps: LogsTableProps = useMemo(() => {
    return {
      loading,
      logs,
      searchText,
    };
  }, [loading, logs, searchText]);

  const errorMessage = error && (
    <div className="logging-help">
      <Alert
        message={
          <span>
            <strong>{labels.error}:</strong> {error}
          </span>
        }
        type={ALERT_TYPE_REPORT}
      />
    </div>
  );

  return (
    <>
      {errorMessage}
      <LogsFilter {...logsFilterProps} />
      <LogsTable {...logsTableProps} />
    </>
  );
};
